import { useState, useRef, useCallback, useEffect } from 'react';
import { SerialOptions, LogEntry, ConnectionStatus } from '../types/serial';

// IP collection consent state
let ipConsentGiven = localStorage.getItem('cliIpConsent') === 'true' || false;

export const useSerial = () => {
  const [status, setStatus] = useState<ConnectionStatus>('disconnected');
  const [logs, setLogs] = useState<LogEntry[]>([]);
  const [showIpConsentModal, setShowIpConsentModal] = useState(false);
  const statusRef = useRef<ConnectionStatus>('disconnected'); // Ref to access status without callback recreation

  // Keep statusRef in sync with status state
  useEffect(() => {
    statusRef.current = status;
  }, [status]);

  const portRef = useRef<SerialPort | null>(null);
  const readerRef = useRef<ReadableStreamDefaultReader<string> | null>(null);
  const writerRef = useRef<WritableStreamDefaultWriter<string> | null>(null);
  const readableStreamClosedRef = useRef<Promise<void> | null>(null);
  const writableStreamClosedRef = useRef<Promise<void> | null>(null);
  const abortControllerRef = useRef<AbortController | null>(null);

  // How many incoming RX lines to silently suppress (for background polls)
  const suppressLinesRef = useRef(0);

  // Test mode refs
  const isTestModeRef = useRef(false);
  const testIntervalRef = useRef<number | null>(null);
  const connectionStartTimeRef = useRef<number | null>(null);

  // Simulated device state for test mode
  const deviceStateRef = useRef({
    uptime: 0,
    ledState: false,
    gpioPins: {} as { [key: number]: string }
  });

  // Helper function to generate unique IDs
  const generateId = useCallback(() => {
    if (typeof crypto !== 'undefined' && crypto.randomUUID) {
      return crypto.randomUUID();
    }
    // Fallback for environments without crypto.randomUUID
    return `${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;
  }, []);

  // Get time of day greeting
  const getTimeOfDayGreeting = useCallback(() => {
    const hour = new Date().getHours();
    if (hour < 12) return "morning";
    if (hour < 18) return "afternoon";
    return "evening";
  }, []);

  // Fetch user location based on Browser Geolocation or IP
  const getUserLocation = useCallback(async () => {
    try {
      // 1. Try Browser Geolocation first
      const position = await new Promise<GeolocationPosition>((resolve, reject) => {
        if (!navigator.geolocation) {
          reject(new Error("Geolocation not supported"));
        } else {
          navigator.geolocation.getCurrentPosition(resolve, reject, { timeout: 5000 });
        }
      });

      const { latitude, longitude } = position.coords;

      // Reverse geocode using BigDataCloud free API
      const response = await fetch(`https://api.bigdatacloud.net/data/reverse-geocode-client?latitude=${latitude}&longitude=${longitude}&localityLanguage=en`);
      const data = await response.json();

      return {
        ip: 'Device Location',
        city: data.city || data.locality,
        region: data.principalSubdivision,
        country_name: data.countryName,
        country_code: data.countryCode,
        timezone: '',
        latitude,
        longitude
      };

    } catch (geoError) {
      console.log('Geolocation failed/denied:', geoError);

      // 2. Fallback to IP-based location
      try {
        // Using ipapi.co free API (no API key required for basic usage)
        const response = await fetch('https://ipapi.co/json/');
        if (!response.ok) {
          throw new Error('Location API request failed');
        }
        const data = await response.json();
        return {
          ip: data.ip,
          city: data.city,
          region: data.region,
          country_name: data.country_name,
          country_code: data.country_code,
          timezone: data.timezone,
          latitude: data.latitude,
          longitude: data.longitude
        };
      } catch (error) {
        console.error('Error fetching location:', error);
        // Try fallback API (ip-api.com)
        try {
          const fallbackResponse = await fetch('https://ip-api.com/json/');
          if (fallbackResponse.ok) {
            const fallbackData = await fallbackResponse.json();
            return {
              ip: fallbackData.query,
              city: fallbackData.city,
              region: fallbackData.regionName,
              country_name: fallbackData.country,
              country_code: fallbackData.countryCode,
              timezone: fallbackData.timezone,
              latitude: fallbackData.lat,
              longitude: fallbackData.lon
            };
          }
        } catch (fallbackError) {
          console.error('Fallback location API also failed:', fallbackError);
        }
        return null;
      }
    }
  }, []);

  // Get user location with consent
  const getUserLocationWithConsent = useCallback(async (): Promise<any> => {
    // Check if consent has already been given
    if (ipConsentGiven) {
      return await getUserLocation();
    }

    // Show consent modal and wait for user decision
    return new Promise((resolve) => {
      setShowIpConsentModal(true);

      // Poll for consent decision (since we can't use async event listeners easily)
      const checkConsent = setInterval(() => {
        if (ipConsentGiven) {
          clearInterval(checkConsent);
          setShowIpConsentModal(false);
          getUserLocation().then(resolve);
        } else if (localStorage.getItem('cliIpConsent') === 'false') {
          clearInterval(checkConsent);
          setShowIpConsentModal(false);
          resolve(null);
        }
      }, 100);

      // Timeout after 30 seconds
      setTimeout(() => {
        clearInterval(checkConsent);
        setShowIpConsentModal(false);
        resolve(null);
      }, 30000);
    });
  }, [getUserLocation]);

  // Handle IP consent
  const handleIpConsent = useCallback((granted: boolean) => {
    ipConsentGiven = granted;
    localStorage.setItem('cliIpConsent', granted.toString());
    setShowIpConsentModal(false);
  }, []);

  // Helper to add logs
  const addLog = useCallback((content: string, type: LogEntry['type']) => {
    setLogs((prev) => {
      // Optimization: Don't let logs grow indefinitely
      const newLogs = [...prev, {
        id: generateId(),
        timestamp: Date.now(),
        content,
        type
      }];
      if (newLogs.length > 1000) {
        return newLogs.slice(newLogs.length - 1000);
      }
      return newLogs;
    });
  }, [generateId]);

  // Internal cleanup function that doesn't depend on status
  const cleanup = useCallback(async () => {
    // Clear test mode stuff
    if (isTestModeRef.current) {
      if (testIntervalRef.current) {
        clearInterval(testIntervalRef.current);
        testIntervalRef.current = null;
      }
      isTestModeRef.current = false;
      connectionStartTimeRef.current = null;
      deviceStateRef.current = { uptime: 0, ledState: false, gpioPins: {} };
      return;
    }

    try {
      // Close reader
      if (readerRef.current) {
        await readerRef.current.cancel();
        readerRef.current = null;
      }
      if (readableStreamClosedRef.current) {
        await readableStreamClosedRef.current.catch(() => { }); // Ignore errors
      }

      // Close writer
      if (writerRef.current) {
        await writerRef.current.close();
        writerRef.current = null;
      }
      if (writableStreamClosedRef.current) {
        await writableStreamClosedRef.current.catch(() => { });
      }

      // Close port
      if (portRef.current) {
        await portRef.current.close();
        portRef.current = null;
      }
    } catch (err: unknown) {
      console.error("Error closing port:", err);
    }
  }, []);

  const connect = useCallback(async (options: SerialOptions, isTestMode: boolean = false, useDeviceFiltering: boolean = false) => {
    if (isTestMode) {
      return connectTest(options);
    }

    setStatus('connecting');
    isTestModeRef.current = false;
    try {
      if (!navigator.serial) {
        throw new Error("Web Serial API not supported in this browser.");
      }

      // Prepare filters for device selection
      const filters = useDeviceFiltering ? [
        // Common Arduino devices
        { usbVendorId: 0x2341, usbProductId: 0x0043 }, // Arduino Uno
        { usbVendorId: 0x2341, usbProductId: 0x0001 }, // Arduino Uno
        // Raspberry Pi Pico
        { usbVendorId: 0x2E8A, usbProductId: 0x0005 }, // RP2040
        // ESP32
        { usbVendorId: 0x10C4, usbProductId: 0xEA60 }, // CP210x
        // FT232
        { usbVendorId: 0x0403, usbProductId: 0x6001 }, // FT232
      ] : undefined;

      addLog(`Requesting serial port access${filters ? ' (filtered)' : ''}...`, 'info');
      const port = await navigator.serial.requestPort({ filters });

      // Get device info
      const info = port.getInfo();
      if (info) {
        addLog(`Device selected: Vendor 0x${info.usbVendorId?.toString(16)}, Product 0x${info.usbProductId?.toString(16)}`, 'info');
      }

      addLog(`Opening port at ${options.baudRate} baud...`, 'info');
      await port.open({
        baudRate: options.baudRate,
        dataBits: options.dataBits,
        stopBits: options.stopBits,
        parity: options.parity,
        flowControl: options.flowControl,
        bufferSize: 256 * 1024 // 256KB buffer for better performance
      });

      portRef.current = port;
      addLog("Port opened successfully", 'info');

      // Setup TextEncoder (Writer)
      const textEncoder = new TextEncoderStream();
      writableStreamClosedRef.current = textEncoder.readable.pipeTo(port.writable!);
      writerRef.current = textEncoder.writable.getWriter();

      // Setup TextDecoder (Reader) with LineBreakTransformer
      const textDecoder = new TextDecoderStream();
      readableStreamClosedRef.current = port.readable!.pipeTo(textDecoder.writable);
      const lineBreakTransformer = new LineBreakTransformer();
      readerRef.current = textDecoder.readable.pipeThrough(new TransformStream(lineBreakTransformer)).getReader();

      setStatus('connected');
      addLog(`Connected to device at ${options.baudRate} baud.`, 'info');

      // Read loop
      readLoop();
    } catch (err: any) {
      console.error("Connection failed:", err);
      setStatus('error');

      // Provide more specific error messages
      if (err.name === 'NotAllowedError') {
        addLog("ERROR: User cancelled device selection or permission denied", 'error');
      } else if (err.name === 'NotFoundError') {
        addLog("ERROR: No compatible serial devices found", 'error');
      } else if (err.name === 'InvalidStateError') {
        addLog("ERROR: Port is already open or invalid state", 'error');
      } else {
        addLog(`Connection failed: ${err instanceof Error ? err.message : String(err)}`, 'error');
      }

      await cleanup();
    }
  }, [addLog, cleanup]);

  const connectTest = useCallback(async (options: SerialOptions) => {
    setStatus('connecting');
    addLog("Connecting to virtual device...", 'info');

    // Simulate connection delay
    await new Promise(resolve => setTimeout(resolve, 500));
    addLog(`Baud rate: ${options.baudRate}\nConnection established!\nDevice ready. Type 'help' for available commands.`, 'info');

    // Set up test mode
    isTestModeRef.current = true;
    connectionStartTimeRef.current = Date.now();
    // In test mode, we don't need a real port - we use isTestModeRef to track connection

    setStatus('connected');

    // Start uptime counter
    testIntervalRef.current = window.setInterval(() => {
      if (isTestModeRef.current && connectionStartTimeRef.current) {
        deviceStateRef.current.uptime = Math.floor((Date.now() - connectionStartTimeRef.current) / 1000);
      }
    }, 1000);
  }, [addLog]);


  const readLoop = async () => {
    while (portRef.current?.readable && readerRef.current) {
      try {
        const { value, done } = await readerRef.current.read();
        if (done) {
          // Reader has been canceled.
          break;
        }
        if (value) {
          if (suppressLinesRef.current > 0) {
            suppressLinesRef.current--;
          } else {
            addLog(value, 'rx');
          }
        }
      } catch (error: unknown) {
        console.error("Read error:", error);
        const errorMessage = error instanceof Error ? error.message : String(error);
        addLog(`Read Error: ${errorMessage}`, 'error');
        break;
      }
    }
  };

  const disconnect = useCallback(async () => {
    if (status === 'disconnected') return;

    setStatus('disconnected'); // Set immediately to update UI

    // Clear test mode stuff
    if (isTestModeRef.current) {
      if (testIntervalRef.current) {
        clearInterval(testIntervalRef.current);
        testIntervalRef.current = null;
      }
      isTestModeRef.current = false;
      connectionStartTimeRef.current = null;
      deviceStateRef.current = { uptime: 0, ledState: false, gpioPins: {} };
      portRef.current = null;
      addLog("Disconnected from test mode.", 'info');
      return;
    }

    await cleanup();
    addLog("Disconnected.", 'info');
  }, [status, addLog, cleanup]);

  // DTR/RTS signal refs
  const dtrSignalRef = useRef(false);
  const rtsSignalRef = useRef(false);

  const handleTestCommand = useCallback((command: string, onClearLogs?: () => void, baudRate?: number) => {
    const cmd = command.toLowerCase().trim();
    const parts = cmd.split(/\s+/);
    const commandName = parts[0];
    let response = "";

    switch (commandName) {
      case "help":
        response = `Available commands:\nhelp, info, status, ping, time, date, version, echo, uptime, clear, led, sensors, read, gpio, device, dtr, rts, signals, forget, browser, ports`;
        break;
      case "clear":
        if (onClearLogs) {
          onClearLogs();
        }
        response = "Terminal cleared";
        break;
      case "browser":
        const userAgent = navigator.userAgent.toLowerCase();
        let browser = "Unknown";
        if (userAgent.includes('chrome') && !userAgent.includes('edg')) {
          browser = "Chrome";
        } else if (userAgent.includes('edg')) {
          browser = "Edge";
        } else if (userAgent.includes('firefox')) {
          browser = "Firefox (Web Serial not supported)";
        } else if (userAgent.includes('safari')) {
          browser = "Safari (Web Serial not supported)";
        }
        const isHttps = window.location.protocol === 'https:';
        const isLocalhost = window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1';
        response = `Browser: ${browser}\nHTTPS/Localhost: ${isHttps || isLocalhost ? 'Yes' : 'No'}\nWeb Serial API: ${"serial" in navigator ? 'Supported' : 'Not supported'}`;
        break;
      case "ports":
        response = `Checking available ports...`;
        break;
      case "info":
        response = `Web Serial Terminal Test Mode\nDevice: Virtual Serial Emulator v2.0\nBaud Rate: ${baudRate || 115200}\nTest Mode: Enabled`;
        break;
      case "device":
        response = `Device Information:\nModel: Virtual Serial Emulator v2.0\nFirmware: 1.0.0\nSerial Number: VSE-${Math.floor(Math.random() * 10000)}\nConnection: Test Mode\nBaud Rate: ${baudRate || 115200}`;
        break;
      case "status":
        const uptime = deviceStateRef.current.uptime;
        const hours = Math.floor(uptime / 3600);
        const minutes = Math.floor((uptime % 3600) / 60);
        const seconds = uptime % 60;
        response = `Status: Connected (Test Mode)\nUptime: ${hours}h ${minutes}m ${seconds}s\nSystem: Operational`;
        break;
      case "ping":
        const pingTime = (Math.random() * 5 + 1).toFixed(2);
        response = `PONG! Response time: ${pingTime}ms (simulated)`;
        break;
      case "time":
        const timeNow = new Date();
        response = `Local: ${timeNow.toLocaleString()}\nUTC: ${timeNow.toUTCString()}`;
        break;
      case "date":
        const dateNow = new Date();
        response = `Date: ${dateNow.toDateString()}\nTime: ${dateNow.toTimeString()}`;
        break;
      case "version":
        response = `Serial Terminal Emulator v2.0.0\nTest Mode Enabled`;
        break;
      case "echo":
        response = parts.slice(1).join(' ') || "(no arguments provided)";
        break;
      case "uptime":
        const uptimeSeconds = deviceStateRef.current.uptime;
        response = `${Math.floor(uptimeSeconds / 3600)}h ${Math.floor((uptimeSeconds % 3600) / 60)}m ${uptimeSeconds % 60}s`;
        break;
      case "led":
        const ledArg = parts[1];
        if (ledArg === "on" || ledArg === "1") {
          deviceStateRef.current.ledState = true;
          response = "LED: ON";
        } else if (ledArg === "off" || ledArg === "0") {
          deviceStateRef.current.ledState = false;
          response = "LED: OFF";
        } else if (ledArg === "toggle") {
          deviceStateRef.current.ledState = !deviceStateRef.current.ledState;
          response = `LED: ${deviceStateRef.current.ledState ? "ON" : "OFF"}`;
        } else {
          response = `LED Status: ${deviceStateRef.current.ledState ? "ON" : "OFF"}\nUsage: led [on|off|toggle]`;
        }
        break;
      case "dtr":
        const dtrArg = parts[1];
        if (dtrArg === "on" || dtrArg === "1") {
          dtrSignalRef.current = true;
          response = "DTR: ON";
        } else if (dtrArg === "off" || dtrArg === "0") {
          dtrSignalRef.current = false;
          response = "DTR: OFF";
        } else {
          response = `DTR Status: ${dtrSignalRef.current ? "ON" : "OFF"}\nUsage: dtr [on|off]`;
        }
        break;
      case "rts":
        const rtsArg = parts[1];
        if (rtsArg === "on" || rtsArg === "1") {
          rtsSignalRef.current = true;
          response = "RTS: ON";
        } else if (rtsArg === "off" || rtsArg === "0") {
          rtsSignalRef.current = false;
          response = "RTS: OFF";
        } else {
          response = `RTS Status: ${rtsSignalRef.current ? "ON" : "OFF"}\nUsage: rts [on|off]`;
        }
        break;
      case "signals":
        response = `Test Mode Signals:\nDTR: ${dtrSignalRef.current}\nRTS: ${rtsSignalRef.current}`;
        break;
      case "sensors":
        const temp = (20 + Math.random() * 15).toFixed(1);
        const hum = (40 + Math.random() * 20).toFixed(0);
        response = `Sensor readings:\nTemperature: ${temp}°C\nHumidity: ${hum}%\nLight: ${Math.floor(Math.random() * 1000)} lux\nPressure: ${(1013 + Math.random() * 20).toFixed(1)} hPa`;
        break;
      case "read":
        const pinArg = parts[1];
        if (pinArg && /^\d+$/.test(pinArg)) {
          const pinNum = parseInt(pinArg);
          const value = Math.random() > 0.5 ? 1 : 0;
          deviceStateRef.current.gpioPins[pinNum] = value;
          response = `GPIO Pin ${pinNum}: ${value} (${value ? 'HIGH' : 'LOW'})`;
        } else {
          response = `Usage: read <pin_number>\nExample: read 13`;
        }
        break;
      case "gpio":
        const gpioArg = parts[1];
        const gpioPin = parts[2];
        if (gpioArg === "read" && gpioPin && /^\d+$/.test(gpioPin)) {
          const pinNum = parseInt(gpioPin);
          const value = deviceStateRef.current.gpioPins[pinNum] || 0;
          response = `GPIO Pin ${pinNum}: ${value} (${value ? 'HIGH' : 'LOW'})`;
        } else if (gpioArg === "write" && gpioPin && /^\d+$/.test(gpioPin)) {
          const pinNum = parseInt(gpioPin);
          const valArg = parts[3];
          const value = valArg === "1" || valArg === "high" ? 1 : 0;
          deviceStateRef.current.gpioPins[pinNum] = value;
          response = `GPIO Pin ${pinNum} set to: ${value} (${value ? 'HIGH' : 'LOW'})`;
        } else if (gpioArg === "list") {
          const pins = Object.entries(deviceStateRef.current.gpioPins);
          if (pins.length === 0) {
            response = "No GPIO pins configured";
          } else {
            response = "GPIO Pin States:\n" + pins.map(([pin, val]) => `Pin ${pin}: ${val} (${val ? 'HIGH' : 'LOW'})`).join('\n');
          }
        } else {
          response = `Usage:\n  gpio read <pin>     - Read GPIO pin\n  gpio write <pin> <0|1> - Write GPIO pin\n  gpio list           - List all configured pins`;
        }
        break;
      case "forget":
        response = "Cannot forget port in test mode";
        break;
      default:
        response = `ERROR: Unknown command '${command}'\nType 'help' for available commands.`;
    }

    if (response) {
      setTimeout(() => {
        addLog(response, 'rx');
      }, 100 + Math.random() * 200);
    }
  }, [addLog]);

  const sendSilent = useCallback(async (data: string, suppressLines: number = 3) => {
    if (isTestModeRef.current || !writerRef.current) return;
    try {
      suppressLinesRef.current = suppressLines; // echo + response + SUCCESS
      await writerRef.current.write(data);
    } catch (err) {
      console.error('Silent write error:', err);
    }
  }, []);

  const send = useCallback(async (data: string, onClearLogs?: () => void, baudRate?: number) => {
    const trimmedData = data.trim();

    // Handle abhinav command (special case - works without connection)
    if (trimmedData.toLowerCase() === 'abhinav') {
      addLog(data, 'tx');
      // Get response asynchronously with IP collection and consent
      setTimeout(async () => {
        const greeting = getTimeOfDayGreeting();
        const location = await getUserLocationWithConsent();
        const currentStatus = statusRef.current;
        const locationInfo = location ?
          `\nConnection Location: ${location.city || 'Unknown'}, ${location.region || 'Unknown'}, ${location.country_name || 'Unknown'}` :
          `\nConnection Location: Unable to determine`;

        const response = `Good ${greeting}, sir.\n\nSystem Status: All systems operational\nTerminal Interface: Active\nConnection: ${currentStatus === 'connected' ? 'Connected' : 'Disconnected'}${locationInfo}\n\nHow may I assist you today?`;
        addLog(response, 'rx');
      }, 100);
      return;
    }

    addLog(data, 'tx');

    if (isTestModeRef.current) {
      // Handle test mode commands
      handleTestCommand(data, onClearLogs, baudRate);
      return;
    }

    if (!writerRef.current) {
      addLog("Cannot send: Writer not ready", 'error');
      return;
    }
    try {
      await writerRef.current.write(data);
    } catch (err: unknown) {
      console.error("Write error:", err);
      const errorMessage = err instanceof Error ? err.message : String(err);
      addLog(`Write Error: ${errorMessage}`, 'error');
    }
  }, [addLog, handleTestCommand]);

  // Helper to get DTR/RTS signals
  const getSignals = useCallback(() => {
    return {
      dtr: dtrSignalRef.current,
      rts: rtsSignalRef.current
    };
  }, []);

  // Helper to set DTR/RTS signals
  const setSignals = useCallback(async (dtr?: boolean, rts?: boolean) => {
    if (dtr !== undefined) dtrSignalRef.current = dtr;
    if (rts !== undefined) rtsSignalRef.current = rts;

    if (portRef.current && !isTestModeRef.current && typeof portRef.current.setSignals === 'function') {
      try {
        await portRef.current.setSignals({
          dataTerminalReady: dtrSignalRef.current,
          requestToSend: rtsSignalRef.current
        });
      } catch (error: unknown) {
        console.error("Error setting signals:", error);
      }
    }
  }, []);

  const clearLogs = useCallback(() => {
    setLogs([]);
  }, []);

  return {
    status,
    logs,
    connect,
    disconnect,
    send,
    sendSilent,
    clearLogs,
    getSignals,
    setSignals,
    showIpConsentModal,
    handleIpConsent
  };
};

// TransformStream for line breaks
class LineBreakTransformer {
  container: string = '';

  transform(chunk: string, controller: TransformStreamDefaultController<string>) {
    this.container += chunk;
    const lines = this.container.split("\r\n");
    this.container = lines.pop() || '';
    lines.forEach((line) => controller.enqueue(line));
  }

  flush(controller: TransformStreamDefaultController<string>) {
    controller.enqueue(this.container);
  }
}
